import { useState } from 'react'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, downloadCsv } from '@/components/reports/procedures/reportShared'
import { useInventoryExpiryReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import type { ReportTimeFrame } from '@/lib/reportRange'

/**
 * Report 2.3 — expiration and expiry risk.
 *
 * ## The period looks FORWARD here, and the report says so
 *
 * Every other report on this page reads backwards. Expiry risk is the opposite
 * question, so the period's LENGTH becomes a horizon measured from today:
 * "last 30 days" asks about the next 30. That is surprising enough to be
 * stated in the subtitle rather than left for a reader to work out from
 * numbers that look wrong.
 *
 * ## Expired and expiring are two different jobs
 *
 * Expired stock is a disposal and a gap to fill; expiring stock is a plan.
 * They are counted, coloured and sorted apart rather than summed into one
 * "at risk" figure that would tell nobody what to do today.
 */
const EXPIRED = '#d64545'
const EXPIRING = '#eda100'

const PAGE_SIZE = 20

/** "in 12 days", "today", "9 days ago" — a direction, not a signed integer. */
function whenLabel(days: number): string {
  if (days === 0) return 'today'
  return days > 0 ? `in ${days}d` : `${Math.abs(days)}d ago`
}

export function InventoryExpiryReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useInventoryExpiryReport(frame, filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const pageCount = Math.max(1, Math.ceil(report.rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.rows.slice(start, start + PAGE_SIZE)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Lot', 'Material', 'Category', 'Quantity', 'Expires', 'Days', 'Status'],
        report.rows.map((r) => [
          r.lotNumber,
          r.materialName,
          r.category,
          r.quantity,
          r.expiresOn,
          r.daysToExpiry,
          r.status === 'expired' ? 'Expired' : 'Expiring',
        ]),
      ),
      reportFileName('inventory-expiry', report.window),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <ReportSection
        title='Expiry risk'
        // The one report here whose period points forward, so the subtitle
        // states the direction instead of the dates.
        subtitle={`Next ${report.horizonDays} days, from ${report.asOf}`}
      >
        <div className='flex flex-wrap items-end gap-x-8 gap-y-3 p-4'>
          <div>
            <p
              className='text-5xl font-semibold tabular-nums'
              style={{ color: report.expiredLots > 0 ? EXPIRED : undefined }}
            >
              {report.expiredLots}
            </p>
            <p className='mt-1 text-sm text-muted-foreground'>
              lots already expired, {report.expiredQty} units
            </p>
          </div>
          <dl className='flex flex-wrap gap-x-6 gap-y-2'>
            <Stat label={`Expiring in ${report.horizonDays}d`} value={report.expiringLots} color={EXPIRING} />
            <Stat label='Units expiring' value={report.expiringQty} />
            <Stat label='No expiry recorded' value={report.noExpiryLots} />
          </dl>
        </div>
      </ReportSection>

      {report.byMaterial.length > 0 && (
        <ReportSection title='By material'>
          <div className='overflow-x-persistent'>
            <table className='w-full text-xs'>
              <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                <tr>
                  <th className='px-3 py-2 font-medium'>Material</th>
                  <th className='px-3 py-2 font-medium'>Category</th>
                  <th className='px-3 py-2 text-right font-medium'>Expired</th>
                  <th className='px-3 py-2 text-right font-medium'>Expiring</th>
                  <th className='px-3 py-2 text-right font-medium'>Lots</th>
                  <th className='px-3 py-2 font-medium'>Act by</th>
                </tr>
              </thead>
              <tbody>
                {report.byMaterial.map((m) => (
                  <tr key={m.materialId} className='border-b last:border-0'>
                    <td className='px-3 py-1.5'>{m.materialName}</td>
                    <td className='px-3 py-1.5'>{m.category}</td>
                    <td
                      className='px-3 py-1.5 text-right tabular-nums'
                      style={{ color: m.expiredQty > 0 ? EXPIRED : undefined }}
                    >
                      {m.expiredQty || '—'}
                    </td>
                    <td
                      className='px-3 py-1.5 text-right tabular-nums'
                      style={{ color: m.expiringQty > 0 ? EXPIRING : undefined }}
                    >
                      {m.expiringQty || '—'}
                    </td>
                    <td className='px-3 py-1.5 text-right tabular-nums'>{m.lots}</td>
                    <td className='px-3 py-1.5 tabular-nums'>{m.soonestExpiry}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>
      )}

      <ReportSection title='Lots' onExport={report.rows.length > 0 ? exportCsv : undefined}>
        {report.rows.length === 0 ? (
          <p className='px-4 py-6 text-xs text-muted-foreground'>
            Nothing expired, and nothing expiring in {report.horizonDays} days.
          </p>
        ) : (
          <>
            <div className='overflow-x-persistent'>
              <table className='w-full text-xs'>
                <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Lot</th>
                    <th className='px-3 py-2 font-medium'>Material</th>
                    <th className='px-3 py-2 text-right font-medium'>Quantity</th>
                    <th className='px-3 py-2 font-medium'>Expires</th>
                    <th className='px-3 py-2 font-medium'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.lotId} className='border-b last:border-0'>
                      <td className='px-3 py-1.5 tabular-nums'>{r.lotNumber}</td>
                      <td className='px-3 py-1.5'>
                        {r.materialName}
                        <span className='ml-1.5 text-muted-foreground'>{r.category}</span>
                      </td>
                      <td className='px-3 py-1.5 text-right tabular-nums'>{r.quantity}</td>
                      <td className='px-3 py-1.5 tabular-nums'>
                        {r.expiresOn}
                        <span className='ml-1.5 text-muted-foreground'>{whenLabel(r.daysToExpiry)}</span>
                      </td>
                      <td
                        className='px-3 py-1.5 font-medium'
                        style={{ color: r.status === 'expired' ? EXPIRED : EXPIRING }}
                      >
                        {r.status === 'expired' ? 'Expired' : 'Expiring'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ReportPager
              page={current}
              pageCount={pageCount}
              from={start + 1}
              to={start + rows.length}
              total={report.rows.length}
              onPage={setPage}
            />
          </>
        )}
      </ReportSection>
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <dt className='flex items-center gap-1.5 text-xs text-muted-foreground'>
        {color && <span aria-hidden className='h-2 w-2 rounded-sm' style={{ background: color }} />}
        {label}
      </dt>
      <dd className='text-xl font-semibold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
